/**
 * Manapool orders API client
 */
import { api } from '../services/api';

export interface ManapoolPendingResponse {
  orders: Array<Record<string, unknown>>;
  total: number;
  skip: number;
  limit: number;
}

export const listPendingManapoolOrders = async (skip = 0, limit = 20, search = ''): Promise<ManapoolPendingResponse> => {
  const response = await api.get<ManapoolPendingResponse>('/orders/manapool/pending', {
    params: { skip, limit, search: search || undefined },
  });
  return response.data;
};

export const markManapoolItemUnfound = async (
  orderId: string,
  itemId: string,
  quantityFound: number,
  notes?: string,
) => {
  const response = await api.post(
    `/orders/manapool/${orderId}/items/${itemId}/mark-unfound`,
    null,
    {
      params: {
        quantity_found: quantityFound,
        notes,
      },
    },
  );
  return response.data;
};

export const markManapoolOrderReplaced = async (orderId: string) => {
  const response = await api.post(`/orders/manapool/${orderId}/mark-replaced`);
  return response.data as {
    status: string;
    order_id: string;
    external_id: string;
    commercial_status: string;
    fulfillment_status: string;
    shortfall_lines: number;
  };
};

export interface ManapoolReportLine {
  report_id: string;
  manapool_order_item_id: string | null;
  quantity: number;
  matched_order_item_id: string | null;
  matched_external_line_id?: string | null;
  card_name?: string | null;
  proposed_remediation_method?: string | null;
  rescinded?: boolean;
  has_remediations?: boolean;
  comment?: string | null;
}

export interface ManapoolOrderReportsResponse {
  order_id: string;
  external_id: string;
  label?: string | null;
  fulfillment_status: string;
  commercial_status: string;
  reports: Record<string, unknown>;
  lines: ManapoolReportLine[];
  order_items: Array<{
    id: string;
    external_line_id?: string | null;
    card_name?: string | null;
    requested_qty: number;
    allocated_qty: number;
    shortfall_qty: number;
  }>;
  detected_terminal_status?: string | null;
}

export const fetchManapoolOrderReports = async (
  orderId: string,
): Promise<ManapoolOrderReportsResponse> => {
  const response = await api.get<ManapoolOrderReportsResponse>(
    `/orders/manapool/${orderId}/reports`,
  );
  return response.data;
};

export const applyManapoolOrderReports = async (
  orderId: string,
  payload: {
    report_id?: string | null;
    lines: Array<{ line_id: string; quantity: number; restock: boolean }>;
  },
) => {
  const response = await api.post(`/orders/manapool/${orderId}/reports/apply`, payload);
  return response.data as {
    status: string;
    order_id: string;
    external_id: string;
    restocked_total: number;
    shortfall_total: number;
    lines: Array<Record<string, unknown>>;
  };
};

export const retryManapoolItemAllocation = async (orderId: string, itemId: string) => {
  const response = await api.post(
    `/orders/manapool/${orderId}/items/${itemId}/retry-allocation`,
  );
  return response.data as {
    order_id: string;
    external_id: string;
    order_item_id: string;
    product_id: string;
    variant_id: string | null;
    previously_allocated: number;
    newly_allocated: number;
    allocated_qty: number;
    shortfall_qty: number;
    issues: string[];
    outbox_event_id: string | null;
    fulfillment_status?: string;
    decision_reasons?: string[];
  };
};
